// /app/frontend/src/features/choral-editorial/EditableField.jsx
//
// Scalar field editor. Arrays get their own chip editor; do NOT force
// arrays through this control. Every save requires a reason and is
// audited on the backend.
import React, { useState } from "react";

export default function EditableField({
  workId,
  field,
  label,
  value,
  readOnly = false,
  onSaved,
  apiBase = "/api/admin/choral",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(
    value == null ? "" : (typeof value === "string" ? value : JSON.stringify(value))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const displayValue =
    value == null || value === "" ? (
      <span className="ce-field__value ce-field__value--unknown">Not yet known</span>
    ) : (
      <span className="ce-field__value">
        {typeof value === "string" ? value : JSON.stringify(value)}
      </span>
    );

  async function save() {
    const reason = window.prompt("Reason for this editorial change:");
    if (!reason) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/${workId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, value: draft, reason }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setEditing(false);
      onSaved?.(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ce-field" data-testid={`ce-field-${field}`}>
      <div className="ce-field__label">{label}</div>
      <div>
        {editing ? (
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            aria-label={`${label} value`}
            data-testid={`ce-field-input-${field}`}
            style={{
              width: "100%",
              padding: "6px 10px",
              border: "1px solid var(--li-border, #C7BDA6)",
              borderRadius: 3,
              fontFamily: "Inter, sans-serif",
              fontSize: "0.92rem",
            }}
          />
        ) : (
          displayValue
        )}
        {error && (
          <div style={{ color: "#8A5A32", fontSize: "0.8rem", marginTop: 4 }}>
            {error}
          </div>
        )}
      </div>
      {!readOnly &&
        (editing ? (
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="ce-field__edit"
              onClick={save}
              disabled={saving}
              data-testid={`ce-field-save-${field}`}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              className="ce-field__edit"
              onClick={() => {
                setEditing(false);
                setDraft(value ?? "");
                setError(null);
              }}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            className="ce-field__edit"
            onClick={() => setEditing(true)}
            data-testid={`ce-field-edit-${field}`}
          >
            Edit
          </button>
        ))}
    </div>
  );
}
